/**
 * Monaco Signature Help Provider
 *
 * Shows function parameter hints when the user types inside function calls.
 * E.g., typing SUM( shows: SUM(number1, [number2], ...)
 * The current parameter is highlighted as the user moves between commas.
 */

import type * as monaco from 'monaco-editor';

import type { OperatorDefinition } from './operatorConstants';
import { FUNCTION_SIGNATURES, buildSignatureLabel } from './functionSignatures';
import { removeStringLiterals } from '../utils/formulaHelpers';

interface EnclosingFunction {
  name: string;
  activeParameter: number;
}

/**
 * Find the enclosing function call at the cursor position.
 * Walks backward through the text, tracking parenthesis depth,
 * to find which function the cursor is inside and which parameter it's at.
 */
function findEnclosingFunction(textUntilCursor: string): EnclosingFunction | null {
  // Strip string literals to avoid counting brackets/parens/commas inside them
  const cleaned = removeStringLiterals(textUntilCursor);

  let parenDepth = 0;
  let commaCount = 0;

  // Walk backward from cursor
  for (let i = cleaned.length - 1; i >= 0; i--) {
    const ch = cleaned[i];

    if (ch === ')' || ch === ']') {
      parenDepth++;
    } else if (ch === '(' || ch === '[') {
      parenDepth--;
      if (parenDepth === -1 && ch === '(') {
        // Found the opening paren — look backward for function name
        const textBefore = cleaned.substring(0, i);
        const funcMatch = textBefore.match(/([A-Z][A-Z0-9]*)\s*$/);
        if (funcMatch) {
          return { name: funcMatch[1], activeParameter: commaCount };
        }
        // Opening paren without a function name (e.g., grouping parens)
        return null;
      }
    } else if (ch === ',' && parenDepth === 0) {
      commaCount++;
    }
  }

  return null;
}

/**
 * Register a SignatureHelpProvider for formula-lang.
 * Returns a disposable to clean up the registration.
 */
export function registerFormulaSignatureHelp(
  monacoInstance: typeof monaco,
  operators: OperatorDefinition[]
): monaco.IDisposable {
  // Build operator lookup for descriptions
  const operatorMap = new Map<string, OperatorDefinition>();
  for (const op of operators) {
    operatorMap.set(op.name, op);
  }

  return monacoInstance.languages.registerSignatureHelpProvider('formula-lang', {
    signatureHelpTriggerCharacters: ['(', ','],
    signatureHelpRetriggerCharacters: [','],

    provideSignatureHelp(model, position) {
      const textUntilPosition = model.getValueInRange({
        startLineNumber: 1,
        startColumn: 1,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      });

      const enclosing = findEnclosingFunction(textUntilPosition);
      if (!enclosing) return null;

      const sig = FUNCTION_SIGNATURES[enclosing.name];
      if (!sig) return null;

      const op = operatorMap.get(enclosing.name);
      const label = buildSignatureLabel(enclosing.name, sig);

      // Build parameter information
      const parameters: monaco.languages.ParameterInformation[] = sig.parameters.map((p) => ({
        label: p.optional ? `[${p.name}]` : p.name,
        ...(p.description ? { documentation: p.description } : {}),
      }));

      // For variadic functions, clamp activeParameter to last param
      const activeParameter = sig.variadic
        ? Math.min(enclosing.activeParameter, Math.max(0, parameters.length - 1))
        : Math.min(enclosing.activeParameter, parameters.length - 1);

      return {
        value: {
          signatures: [
            {
              label,
              documentation: op
                ? { value: `**${op.label}** — ${op.description}\n\n*Example:* \`${op.example}\`` }
                : undefined,
              parameters,
            },
          ],
          activeSignature: 0,
          activeParameter,
        },
        dispose() {},
      };
    },
  });
}
